import React, { useEffect, useRef, useState } from 'react';

// 数字雨字形集合：片假名 + 拉丁字母 + 数字 + 符号
const MATRIX_GLYPHS =
  'アカサタナハマヤラワガザダバパイキシチニヒミリウクスツヌフムユルエケセテネヘメレオコソトノホモヨロABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%&*+=<>';

// 居中标题及其逐字打字速度
const SPLASH_TITLE = 'Kendama Toolbox';
const TITLE_TYPING_INTERVAL_MS = 62;
// 退格速度，比打字稍快
const TITLE_BACKSPACE_INTERVAL_MS = 42;
// 退格前停顿，模拟发现打错后的犹豫
const TITLE_BACKSPACE_HOLD_MS = 340;

// 打字脚本：打到第 12 个字后停顿退格删掉 4 个，再重新补全
const TITLE_TYPING_SCRIPT: ReadonlyArray<{ length: number; delayMs: number }> = [
  ...[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((length) => ({
    length,
    delayMs: TITLE_TYPING_INTERVAL_MS,
  })),
  ...[11, 10, 9, 8].map((length, index) => ({
    length,
    delayMs: index === 0 ? TITLE_BACKSPACE_HOLD_MS : TITLE_BACKSPACE_INTERVAL_MS,
  })),
  ...[9, 10, 11, 12, 13, 14, 15].map((length) => ({
    length,
    delayMs: TITLE_TYPING_INTERVAL_MS,
  })),
];

// 青蓝色调，逐列随机取色形成层次
const MATRIX_COLORS = ['#22d3ee', '#38bdf8', '#0ea5e9', '#2dd4bf', '#67e8f9'];

// 半透明黑幕的不透明度：值越小拖影残留越长
const MATRIX_TRAIL_FADE = 0.02;

// 启动动画最短展示时长，避免会话校验过快时动画一闪而过
export const MATRIX_SPLASH_MIN_DURATION_MS = 2400;

// 启动动画退场过渡总时长（含数字流融入首页背景后约 2s 的渐渐消失），
// 需与 matrix-splash-exit 那段编排里最后一个动画的结束时间一致
export const MATRIX_SPLASH_EXIT_DURATION_MS = 2900;

// 进度跑满后停留的时长。
// 进度条本身的填充过渡是 300ms，这里必须大于它，
// 否则标题和进度条会在进度条还没填满时就开始放大退场。
export const MATRIX_SPLASH_PROGRESS_SETTLE_MS = 380;

interface MatrixRainLoaderProps {
  className?: string;
  // 真实初始化进度（0~1），由实际加载步骤推进
  progress?: number;
  // 加载完成后播放淡出动画
  exiting?: boolean;
}

/* 启动层与首页背景层会短暂同时存在（交接期间），
   所以雨滴位置 / 速度 / 颜色 / 整体速度系数放在模块级共享，两层画的是同一场雨；
   推进交给最早挂载的那一层，避免交接时下落速度翻倍。 */
const rainShared = {
  drops: [] as number[],
  speeds: [] as number[],
  colors: [] as string[],
  // 整体下落速度系数：数字流融入首页背景后会被背景层逐渐调小
  speedScale: 1,
  // 波动：横穿屏幕的行波（相位由驱动层推进，振幅由背景层给出）
  wavePhase: 0,
  waveAmplitude: 0,
};

/* 融入背景后开始减速的时间与幅度：
   等交接完成（620ms）再起步，用和淡出同样的 2s 慢慢降到 18%。 */
const RAIN_SLOWDOWN_DELAY_MS = 620;
const RAIN_SLOWDOWN_DURATION_MS = 2000;
const RAIN_SLOWDOWN_MIN_SCALE = 0.18;
/* 融入背景后的「背景化」处理：拖影更短、头字光晕更弱。
   必须在交接完成之后才生效——交接期间启动层和背景层同时可见，
   两层渲染参数不一致会看出破绽。 */
const RAIN_BACKGROUND_TRAIL_SCALE = 1.75;
const RAIN_BACKGROUND_GLOW = 7;
const RAIN_BACKGROUND_RAMP_MS = 240;
/* 融入背景后字符逐渐缩小（和淡出同一时间窗）：
   边缩边淡，像是整场雨慢慢化掉，而不是单纯变透明。 */
const RAIN_BACKGROUND_SHRINK_DURATION_MS = 2000;
const RAIN_BACKGROUND_MIN_GLYPH = 0.55;
/* 首页出现时的波动：以列为单位横向传播的正弦行波，
   振幅用字号的比例表示。起振时间对齐首页内容开始出现的时刻（比交接完成更早），
   因为相位和振幅都是两层共享的，交接期间启动层与背景层画的是同一道波。 */
const RAIN_WAVE_WAVELENGTH_COLUMNS = 9;
const RAIN_WAVE_PERIOD_MS = 1100;
const RAIN_WAVE_DELAY_MS = 120;
const RAIN_WAVE_MAX_OFFSET = 1.1;
const RAIN_WAVE_RAMP_MS = 320;
const rainInstanceIds: number[] = [];
let rainInstanceSeed = 0;

interface MatrixRainCanvasProps {
  className?: string;
  // 透明背景：用于首页背景层，只留下字符本身
  transparent?: boolean;
}

// 数字雨画布。启动层用不透明黑底，首页背景层用透明底。
export const MatrixRainCanvas: React.FC<MatrixRainCanvasProps> = ({ className, transparent = false }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const instanceIdRef = useRef(0);

  useEffect(() => {
    const instanceId = (rainInstanceSeed += 1);
    instanceIdRef.current = instanceId;
    rainInstanceIds.push(instanceId);
    // 每次首页背景层重新挂载都从原速起步
    if (transparent) rainShared.speedScale = 1;
    return () => {
      const index = rainInstanceIds.indexOf(instanceId);
      if (index >= 0) rainInstanceIds.splice(index, 1);
    };
  }, [transparent]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const context = canvas.getContext('2d');
    if (!context) {
      return;
    }

    let width = 0;
    let height = 0;
    let fontSize = 16;
    let frameId = 0;
    let previousTimestamp = performance.now();
    let startTimestamp = 0;

    const resize = () => {
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = Math.floor(width * ratio);
      canvas.height = Math.floor(height * ratio);
      context.setTransform(ratio, 0, 0, ratio, 0, 0);

      fontSize = width < 640 ? 13 : 16;
      const columnCount = Math.ceil(width / fontSize);
      const rows = height / fontSize;
      // 只有列数变化（尺寸 / 方向变化）才重新撒一遍雨滴，
      // 否则沿用共享状态，让启动层的雨无缝接到首页背景层。
      if (rainShared.drops.length !== columnCount) {
        rainShared.drops = Array.from({ length: columnCount }, () => Math.random() * -rows);
        rainShared.speeds = Array.from({ length: columnCount }, () => 0.55 + Math.random() * 0.95);
        rainShared.colors = Array.from(
          { length: columnCount },
          () => MATRIX_COLORS[Math.floor(Math.random() * MATRIX_COLORS.length)],
        );
      }

      if (transparent) {
        context.clearRect(0, 0, width, height);
      } else {
        context.fillStyle = '#000000';
        context.fillRect(0, 0, width, height);
      }
    };

    const render = (timestamp: number) => {
      const delta = Math.min(timestamp - previousTimestamp, 64);
      previousTimestamp = timestamp;
      const step = delta / 16.67;
      const isDriver = rainInstanceIds[0] === instanceIdRef.current;
      if (isDriver) {
        rainShared.wavePhase = (rainShared.wavePhase + (delta / RAIN_WAVE_PERIOD_MS) * Math.PI * 2) % (Math.PI * 2);
      }

      // 首页背景层负责减速：融入背景后整场雨的下落速度逐渐变慢
      let backgroundLook = 0;
      let glyphScale = 1;
      if (transparent) {
        if (startTimestamp === 0) startTimestamp = timestamp;
        const sinceMount = timestamp - startTimestamp;
        const elapsed = sinceMount - RAIN_SLOWDOWN_DELAY_MS;
        const progress = elapsed <= 0 ? 0 : Math.min(1, elapsed / RAIN_SLOWDOWN_DURATION_MS);
        const eased = 1 - Math.pow(1 - progress, 3);
        rainShared.speedScale = 1 - (1 - RAIN_SLOWDOWN_MIN_SCALE) * eased;
        // 交接完成后才切换成更克制的背景观感
        backgroundLook = Math.max(0, Math.min(1, elapsed / RAIN_BACKGROUND_RAMP_MS));
        // 字符缩小：同样从交接完成后起步，用淡出（2s）的时间缩到 55%
        const shrinkProgress =
          elapsed <= 0 ? 0 : Math.min(1, elapsed / RAIN_BACKGROUND_SHRINK_DURATION_MS);
        const shrinkEased = 1 - Math.pow(1 - shrinkProgress, 2);
        glyphScale = 1 - (1 - RAIN_BACKGROUND_MIN_GLYPH) * shrinkEased;
        // 波动：首页一开始出现就起振，随淡出一起收回
        const waveElapsed = sinceMount - RAIN_WAVE_DELAY_MS;
        const waveIn = Math.max(0, Math.min(1, waveElapsed / RAIN_WAVE_RAMP_MS));
        // 前 30% 的淡出期间保持满振幅，之后才收平
        const waveDecay = Math.max(0, Math.min(1, (shrinkProgress - 0.3) / 0.7));
        rainShared.waveAmplitude = (1 - Math.pow(1 - waveIn, 2)) * (1 - waveDecay);
      }

      // 拖尾：不透明版叠半透明黑幕，透明版把旧字符擦淡。
      // 两者用同一个衰减系数——启动层和背景层画出来的拖尾长度必须一致，
      // 否则交接那段时间会看到两场长短不同的雨叠在一起，
      // 而且背景层拖影过短时，整列只剩最亮的一个字在飘，显得突兀。
      // 交接结束后再把拖影收短一档，让背景不至于糊成一片。
      const trailFade = MATRIX_TRAIL_FADE * (1 + (RAIN_BACKGROUND_TRAIL_SCALE - 1) * backgroundLook);
      if (transparent) {
        context.globalCompositeOperation = 'destination-out';
        context.fillStyle = `rgba(0, 0, 0, ${trailFade})`;
        context.fillRect(0, 0, width, height);
        context.globalCompositeOperation = 'source-over';
      } else {
        context.fillStyle = `rgba(0, 0, 0, ${trailFade})`;
        context.fillRect(0, 0, width, height);
      }
      context.font = `${(fontSize * glyphScale).toFixed(2)}px "SFMono-Regular", Menlo, Consolas, monospace`;
      context.textBaseline = 'top';

      // 头字光晕：背景层比启动层更收敛，避免一个个亮点跳出来
      const headGlow = (14 - (14 - RAIN_BACKGROUND_GLOW) * backgroundLook) * glyphScale;

      const { drops, speeds, colors } = rainShared;
      const waveOffset = RAIN_WAVE_MAX_OFFSET * rainShared.waveAmplitude;
      for (let column = 0; column < drops.length; column += 1) {
        const x = column * fontSize;
        // 行波：同一列整体上下起伏，列与列之间沿屏幕方向错开一个相位
        const wave =
          waveOffset === 0
            ? 0
            : Math.sin((column / RAIN_WAVE_WAVELENGTH_COLUMNS) * Math.PI * 2 - rainShared.wavePhase) *
              waveOffset *
              fontSize;
        const y = drops[column] * fontSize + wave;
        const glyph = MATRIX_GLYPHS.charAt(Math.floor(Math.random() * MATRIX_GLYPHS.length));

        // 头部字符最亮并带荧光
        context.fillStyle = '#e6feff';
        context.shadowColor = colors[column];
        context.shadowBlur = headGlow;
        context.fillText(glyph, x, y);
        context.shadowBlur = 0;

        if (isDriver) {
          drops[column] += speeds[column] * step * rainShared.speedScale;

          // 落到屏幕底部后随机回到顶部重新开始
          if (drops[column] * fontSize > height && Math.random() > 0.972) {
            drops[column] = Math.random() * -12;
          }
        }
      }

      frameId = requestAnimationFrame(render);
    };

    resize();
    frameId = requestAnimationFrame(render);
    window.addEventListener('resize', resize);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', resize);
    };
  }, [transparent]);

  return <canvas ref={canvasRef} className={className} aria-hidden="true" />;
};

// 应用启动初始化界面：黑客帝国风格数字雨
export const MatrixRainLoader: React.FC<MatrixRainLoaderProps> = ({
  className,
  progress = 0,
  exiting = false,
}) => {
  const [typedLength, setTypedLength] = useState(0);

  // 按脚本播放逐字打字，中途会退格纠错
  useEffect(() => {
    let stepIndex = 0;
    let timer = 0;

    const playNextStep = () => {
      const step = TITLE_TYPING_SCRIPT[stepIndex];
      if (!step) {
        return;
      }

      timer = window.setTimeout(() => {
        setTypedLength(step.length);
        stepIndex += 1;
        playNextStep();
      }, step.delayMs);
    };

    playNextStep();

    return () => window.clearTimeout(timer);
  }, []);

  return (
    <div
      className={`fixed inset-0 z-[999] overflow-hidden ${exiting ? 'matrix-splash-exit pointer-events-none' : ''} ${className ?? ''}`}
      aria-busy="true"
      aria-live="polite"
    >
      {/* 黑底单独一层：退场时先化开，让首页从后面透出来 */}
      <div aria-hidden="true" className="matrix-splash-black pointer-events-none absolute inset-0 bg-black" />
      <MatrixRainCanvas className="matrix-splash-rain absolute inset-0 h-full w-full" />
      {/* 扫描线与暗角，强化老式终端质感 */}
      <div
        aria-hidden="true"
        className="matrix-splash-film pointer-events-none absolute inset-0 bg-[repeating-linear-gradient(180deg,rgba(56,189,248,0.05)_0px,rgba(56,189,248,0.05)_1px,transparent_1px,transparent_3px)] opacity-60"
      />
      <div
        aria-hidden="true"
        className="matrix-splash-film pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,0,0,0)_32%,rgba(0,0,0,0.78)_100%)]"
      />
      <div className="matrix-splash-stage relative flex h-full w-full flex-col items-center justify-center gap-8 px-6 text-center">
        <div className="relative">
          {/* 标题周围同样用暗色模糊光晕压暗背景数字雨，与进度条保持一致的凸显效果 */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute left-1/2 top-1/2 h-[clamp(78px,21vmin,136px)] w-[min(660px,96vw)] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0.94)_0%,rgba(0,0,0,0.7)_45%,rgba(0,0,0,0)_78%)] blur-2xl"
          />
          <div className="relative font-orbitron text-[clamp(1.05rem,4.6vmin,2.5rem)] uppercase tracking-[0.4em] text-[#cdf7ff] drop-shadow-[0_0_14px_rgba(34,211,238,0.65)]">
            {/* 用不可见全文占位，避免逐字打字时标题左右跳动 */}
            <span className="invisible" aria-hidden="true">{SPLASH_TITLE}</span>
            <span className="absolute inset-y-0 left-0 flex items-center whitespace-pre">
              {SPLASH_TITLE.slice(0, typedLength)}
              {typedLength < SPLASH_TITLE.length && (
                <span className="matrix-caret ml-1 text-[#22d3ee]">█</span>
              )}
            </span>
          </div>
        </div>
        {/* 进度条外层用暗色模糊光晕压暗背景数字雨，让进度条更凸显 */}
        <div className="relative">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute left-1/2 top-1/2 h-[110px] w-[min(440px,88vw)] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0.94)_0%,rgba(0,0,0,0.7)_45%,rgba(0,0,0,0)_78%)] blur-2xl"
          />
          <div className="relative h-[11px] w-[min(260px,60vw)] overflow-hidden rounded-full bg-[#04161c] ring-1 ring-inset ring-[#22d3ee]/30 shadow-[0_0_20px_rgba(34,211,238,0.28),inset_0_2px_5px_rgba(0,0,0,0.85)]">
            <div
              className="absolute inset-y-0 left-0 w-full origin-left rounded-full bg-[linear-gradient(90deg,rgba(34,211,238,0.15),#22d3ee)] shadow-[0_0_12px_rgba(34,211,238,0.7)] transition-transform duration-300 ease-out"
              style={{ transform: `scaleX(${progress})` }}
            />
          </div>
        </div>
      </div>

      <style>{`
        /* 打字光标闪动 */
        @keyframes matrix-caret {
          0%, 49% { opacity: 1; }
          50%, 100% { opacity: 0; }
        }

        .matrix-caret {
          animation: matrix-caret 620ms steps(1, end) infinite;
        }

        /* 退场编排（总时长 3900ms，需与 MATRIX_SPLASH_EXIT_DURATION_MS 一致）：
           ① 文本与进度条先退场
           ② 黑底化开，交接给首页背景层里的同一场数字雨（两层同速同列，位置不变）
           ③ 数字雨落入背景层先降到 70% 透明度，随即开始渐渐消失，约 3s 后消失干净 */
        @keyframes matrix-splash-stage-out {
          100% { opacity: 0; transform: translate3d(0, -8px, 0) scale(1.15); filter: blur(6px); }
        }
        @keyframes matrix-splash-film-out {
          100% { opacity: 0; }
        }
        @keyframes matrix-splash-fade-out {
          100% { opacity: 0; }
        }

        .matrix-splash-exit {
          will-change: opacity;
        }
        /* ① 文本与进度条：进场最早，离场刻意晚 200ms——
              留一点时间让首页内容先从下面浮上来，标题再压着它退走。
              退场同时叠加动态高斯模糊：边放大边化开，字不是「变小消失」而是「散开消失」 */
        .matrix-splash-exit .matrix-splash-stage {
          transform-origin: center center;
          animation: matrix-splash-stage-out 180ms cubic-bezier(0.4, 0, 0.7, 0.6) 200ms forwards;
        }
        /* ② 黑底、扫描线与启动层这层数字雨同步淡出：
           数字雨和黑底用同一条曲线，两层叠起来的总亮度保持恒定，
           所以交接时看到的是「同一场雨」从最上层慢慢退到首页背景层。 */
        .matrix-splash-exit .matrix-splash-black,
        .matrix-splash-exit .matrix-splash-film,
        .matrix-splash-exit .matrix-splash-rain {
          animation: matrix-splash-film-out 520ms cubic-bezier(0.4, 0, 0.7, 0.5) 100ms forwards;
        }

        /* 动效减弱：只留一次整体淡出 */
        @media (prefers-reduced-motion: reduce) {
          .matrix-splash-exit,
          .matrix-splash-exit .matrix-splash-stage,
          .matrix-splash-exit .matrix-splash-black,
          .matrix-splash-exit .matrix-splash-film,
          .matrix-splash-exit .matrix-splash-rain {
            animation: matrix-splash-fade-out 300ms ease-out forwards;
          }
        }
      `}</style>
    </div>
  );
};
